import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { FirmaRole, AccessLevel, FirmaMe } from '@/types'

interface FirmaContextValue {
  /** Die echte D365-AccountID (aufgelöst vom URL-Token). */
  accountId: string
  /** Ob der Token gerade aufgelöst wird. */
  isResolving: boolean
  /** Fehlermeldung falls Auflösung fehlschlägt. */
  error: string | null
  /** Firma-Rolle des eingeloggten Users (null = nur Superadmin-Sicht). */
  myRole: FirmaRole | null
  /** Individuelle Permissions des eingeloggten Users für diese Firma. */
  myPermissions: Record<string, AccessLevel>
  /** true wenn der User Superadmin oder Firma-Admin ist. */
  isFirmaAdmin: boolean
  /** Gibt true zurück wenn der User eine Section lesen darf. */
  canRead: (section: string) => boolean
  /** Gibt true zurück wenn der User eine Section schreiben darf. */
  canWrite: (section: string) => boolean
  /** true wenn der User via Superadmin-Promotion auto-promoviert wurde. */
  autoPromoted: boolean
}

// Stable no-op fallbacks for the default context value
const _canReadDefault = (_section: string): boolean => false
const _canWriteDefault = (_section: string): boolean => false

const FirmaContext = createContext<FirmaContextValue>({
  accountId: '',
  isResolving: true,
  error: null,
  myRole: null,
  myPermissions: {},
  isFirmaAdmin: false,
  canRead: _canReadDefault,
  canWrite: _canWriteDefault,
  autoPromoted: false,
})

export function useFirmaAccountId(): string {
  return useContext(FirmaContext).accountId
}

export function useFirmaContext(): FirmaContextValue {
  return useContext(FirmaContext)
}

/**
 * Provider, der den URL-Token (:firmaToken) auflöst und den echten accountId bereitstellt.
 * Lädt zusätzlich die per-Firma-Rolle und Permissions des eingeloggten Users.
 */
export function FirmaProvider({ children }: { children: ReactNode }) {
  const { firmaToken } = useParams<{ firmaToken: string }>()
  const [accountId, setAccountId] = useState('')
  const [isResolving, setIsResolving] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!firmaToken) {
      setError('Kein Firma-Token in der URL')
      setIsResolving(false)
      return
    }
    setIsResolving(true)
    setError(null)
    fetch(`/api/auth/resolve-firma/${encodeURIComponent(firmaToken)}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => ({ detail: 'Firma nicht gefunden' }))
          throw new Error(data.detail)
        }
        return res.json()
      })
      .then((data) => {
        setAccountId(data.account_id)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Firma nicht gefunden')
      })
      .finally(() => setIsResolving(false))
  }, [firmaToken])

  const { data: me } = useQuery<FirmaMe>({
    queryKey: ['firmaMe', accountId],
    queryFn: () => api.getFirmaMe(accountId!),
    enabled: !!accountId && !error,
    staleTime: 60_000,  // 1 Minute Cache
  })

  const myRole = me?.role ?? null
  const myPermissions = me?.permissions ?? {}
  const isFirmaAdmin = me?.isFirmaAdmin ?? false
  const autoPromoted = me?.autoPromoted ?? false

  const canRead = useCallback((section: string): boolean => {
    if (isFirmaAdmin) return true
    const level = myPermissions[section]
    return level === 'read' || level === 'write'
  }, [isFirmaAdmin, myPermissions])

  const canWrite = useCallback((section: string): boolean => {
    if (isFirmaAdmin) return true
    return myPermissions[section] === 'write'
  }, [isFirmaAdmin, myPermissions])

  return (
    <FirmaContext.Provider value={{
      accountId,
      isResolving,
      error,
      myRole,
      myPermissions,
      isFirmaAdmin,
      canRead,
      canWrite,
      autoPromoted,
    }}>
      {children}
    </FirmaContext.Provider>
  )
}
